import { isEmpty, omitBy, isNil, find, map, forEach } from "lodash";
import { Col, Row, Spin } from "antd";
import { DataTable, SelectInput } from "@life-line-lab/components-react";
import React, { useState, useEffect } from "react";
import useSession from "Hooks/session.hook";
import { useTranslation } from "react-i18next";
import { ToTitleCase } from "Helpers/to-title-case";
import deliveryTypeJSON from "Data/delivery-types.json";
import orderStatusesMap from "Data/order-statuses.json";
import { AccountRole } from "Types/common.types";
import ReportFilters from "Components/delivery-management/report-tabs/report-filters";
import DataTableFilter from "Components/shared/data-table-filter";
import useSearch from "Hooks/search.hook";
import { LoadingOutlined } from "@ant-design/icons";

const AccessControlledFilters = ({
  filterState,
  reportOptions,
  handleOrgSelectChange,
  companyList,
  customerList,
  handleFilterChange,
  locationList,
}) => {
  const { roles } = useSession();
  const { t } = useTranslation();

  const OwnerFilter = () => {
    return (
      <Col className="mb-0" span={12} xl={6}>
        <div className="mb-7">{t("label.logisticProvider")}</div>
        <SelectInput
          showSearch
          placeholder={t("txt.selectHere")}
          value={filterState?.logisticProvider}
          options={map(reportOptions?.LOGISTIC_PROVIDER, (org) => {
            return {
              label: org.label || "",
              value: org.id,
            };
          })}
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          name="logisticProvider"
          onChange={(value) => handleOrgSelectChange(value, "logisticProvider")}
          allowClear
          mode="multiple"
          showArrow={true}
        />
      </Col>
    );
  };

  const OwnerLogisticsFilter = () => {
    return (
      <Col className="mb-0" span={12} xl={6}>
        <div className="mb-7">{t("label.company")}</div>
        <SelectInput
          showSearch
          placeholder={t("txt.selectHere")}
          value={filterState?.company}
          options={map(companyList, (org) => {
            return {
              label: org.label || "",
              value: org.id,
            };
          })}
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          name="company"
          onChange={(value) => handleOrgSelectChange(value, "company")}
          allowClear
          mode="multiple"
          showArrow={true}
        />
      </Col>
    );
  };

  const CustomerFilter = () => {
    return (
      <Col className="mb-0" span={12} xl={6}>
        <div className="mb-7">{t("txt.customerLocation")}</div>
        <SelectInput
          showSearch
          placeholder={t("txt.selectHere")}
          value={filterState?.customerLocation}
          options={locationList?.map((location) => {
            return {
              value: location.locations._id,
              label:
                location.locations.primary == true
                  ? "Primary Address"
                  : location.locations.locationName,
            };
          })}
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          name="customerLocation"
          onChange={(value) => handleFilterChange(value, "customerLocation")}
          allowClear
          mode="multiple"
          showArrow={true}
        />
      </Col>
    );
  };

  const AllExceptCustomer = () => {
    return (
      <Col className="mb-0" span={12} xl={6}>
        <div className="mb-7">{t("label.customer")}</div>
        <SelectInput
          showSearch
          placeholder={t("txt.selectHere")}
          value={filterState?.customersList}
          options={map(customerList, (org) => {
            return {
              label: org.label || "",
              value: org.id,
            };
          })}
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          name="customersList"
          onChange={(value) => {
            handleOrgSelectChange(value, "customersList");
          }}
          allowClear
          mode="multiple"
          showArrow={true}
        />
      </Col>
    );
  };

  const AllFilter = () => {
    return (
      <Col className="mb-0" span={12} xl={6}>
        <div className="mb-7">{t("label.deliveryTypes")}</div>
        <SelectInput
          showSearch
          placeholder={t("txt.selectHere")}
          value={filterState?.deliveryTypes}
          options={map(deliveryTypeJSON.deliveryTypes, (delivery) => {
            return {
              label: delivery,
              value: delivery,
            };
          })}
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          name="deliveryTypes"
          onChange={(value) => handleFilterChange(value, "deliveryTypes")}
          allowClear
          mode="multiple"
          showArrow={true}
        />
      </Col>
    );
  };

  return (
    <Row>
      <Col span={12} xl={20}>
        <Row gutter={[16, 16]}>
          {roles[0].type == AccountRole.OWNER && <OwnerFilter />}
          {(roles[0].type == AccountRole.OWNER ||
            roles[0].type == AccountRole.LOGISTIC_PROVIDER) && <OwnerLogisticsFilter />}
          {roles[0].type == AccountRole.CUSTOMER ? <CustomerFilter /> : <AllExceptCustomer />}
          <AllFilter />
        </Row>
      </Col>
    </Row>
  );
};

const ReportOrderList = (props) => {
  const {
    columns,
    reportOptions,
    onFilter,
    reportListing,
    locationList,
    onDownloadExcelFile,
    enableDownload = true,
    enableACF = true,
    disableShowLatestStatus = false,
    additionalComponent,
    isLoading,
    handleRefreshList,
    isExcelLoading,
  } = props || {};

  const { t } = useTranslation();
  const { roles } = useSession();

  const [filterState, setFilterState] = useState<{ [key: string]: string | boolean }>({
    status: "",
    sortByCreatedAt: "DESC",
  });

  const [companyList, setCompanyList] = useState([]);
  const [customerList, setCustomerList] = useState([]);

  const { handleSearch } = useSearch({ onFilter, filterState, setFilterState });

  const statusOptions = [{ label: t("option.all"), value: "" }].concat(
    map(orderStatusesMap, (status) => {
      return {
        label: ToTitleCase(status[roles[0].type].replaceAll("_", " ")),
        value: status[roles[0].type],
      };
    }),
  );

  useEffect(() => {
    if (roles[0].type != "CUSTOMER") {
      // Access Control Permissions
      //  LOGISTICS: Company and Customer
      //  COMPANY: Customer
      if (roles[0].type == "LOGISTIC_PROVIDER") {
        setCompanyList(reportOptions.COMPANY);
      }

      reportOptions?.CUSTOMER?.length > 1
        ? setCustomerList(reportOptions?.CUSTOMER.sort((a, b) => (a?.label > b?.label ? 1 : -1)))
        : setCustomerList(reportOptions);
    }
  }, [reportOptions]);

  const handleFilterChange = (value: any, name: string) => {
    let selectedValue;

    switch (name) {
      case "dateFrom":
        selectedValue = value ? value.startOf("day").toISOString() : null;
        break;
      case "dateTo":
        selectedValue = value ? value.endOf("day").toISOString() : null;
        break;
      case "onlyLatestStatus":
        selectedValue = value ? "true" : "false"; // boolean will be cleared by the hook
        break;
      default:
        selectedValue = value;
    }

    if (Array.isArray(value)) {
      selectedValue = value.filter((item) => item !== "all");
    }

    setFilterState({ ...filterState, [name]: selectedValue });
  };

  const handleOrgSelectChange = (value: any, name: string) => {
    if (name == "logisticProvider") {
      let filteredCompanyList = [];

      forEach(value, (id) => {
        let logisticProviderObj = find(reportOptions.LOGISTIC_PROVIDER, (logPro) => {
          return logPro.id == id;
        });
        filteredCompanyList.push(...logisticProviderObj?.subOptions?.options);
      });

      setCompanyList(filteredCompanyList);
      setFilterState({ ...filterState, [name]: value, company: [], customersList: [] });
      return;
    }

    if (name == "company") {
      let filteredCustomerList = [];

      forEach(value, (id) => {
        let companyObj = find(companyList, (compObj) => {
          return compObj.id == id;
        });
        filteredCustomerList.push(...companyObj.subOptions?.options);
      });

      setCustomerList(filteredCustomerList);
      setFilterState({ ...filterState, [name]: value, customersList: [] });
      return;
    }

    setFilterState({ ...filterState, [name]: value });
  };

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input) || isEmpty(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  const sortByCreatedAt = [
    { label: t("option.latest"), value: "DESC" },
    { label: t("option.earliest"), value: "ASC" },
  ];

  return (
    <div>
      <div className="om-report-sort-container">
        <ReportFilters
          handleFilterChange={handleFilterChange}
          filterState={filterState}
          handleDownloadExcelFile={onDownloadExcelFile}
          enableDownload={enableDownload}
          reportListing={reportListing}
          statusOptions={statusOptions}
          sortByCreatedAt={sortByCreatedAt}
          customDateLabel={t("label.orderDate")}
          disableShowLatestStatus={disableShowLatestStatus}
          handleRefreshList={handleRefreshList}
          isLoading={isLoading}
          isExcelLoading={isExcelLoading}
          additionalComponent={additionalComponent}
        />
        <div className="om-divider pad"></div>
        {enableACF && (
          <AccessControlledFilters
            filterState={filterState}
            reportOptions={reportOptions}
            handleOrgSelectChange={handleOrgSelectChange}
            companyList={companyList}
            customerList={customerList}
            handleFilterChange={handleFilterChange}
            locationList={locationList}
          />
        )}
        <DataTableFilter
          searchPlaceholder={t("label.searchReportingOrder")}
          onSearch={(text: string) => handleSearch(text)}
        />
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
          <DataTable
            rowKey={(record: any) => record._key + record.orderLogStatus}
            columns={columns}
            scroll={{ x: true }}
            dataSource={reportListing || []}
          />
      )}
    </div>
  );
};

export default ReportOrderList;
